/**
 * A function to create a log object for the 'portfolioAddedToStage' event (evid: 1)
 *
 * Event description: Portfolio app added to stage by manual installation
 *
 * @param {portfolioAddedToStageParams} params - The parameters for this event
 * @return {LogObject<portfolioAddedToStageParams>} LogObject<portfolioAddedToStageParams>
 */
export function portfolioAddedToStage(params) {
    return { evid: 1, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickCreateCollection' event (evid: 10)
 *
 * Event description: when the user clicks on create collection
 *
 * @param {clickCreateCollectionParams} params - The parameters for this event
 * @property {string} origin - the screen where the project was created from
 * @property {string} section - the section in the screen that the users clicked from.
 * @return {LogObject<clickCreateCollectionParams>} LogObject<clickCreateCollectionParams>
 */
export function clickCreateCollection(params) {
    return { evid: 10, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'collectionCreated' event (evid: 11)
 *
 * Event description: When a portfolio collection is first created in the DB
 *
 * @param {collectionCreatedParams} params - The parameters for this event
 * @property {string} params - Json of:Name, Description
 * @property {string} collectionId - Collection ID
 * @property {string} section - the section in the screen that the users clicked from.
 * @property {string} origin - the screen where the user clicked on 'save collection'.
 * @return {LogObject<collectionCreatedParams>} LogObject<collectionCreatedParams>
 */
export function collectionCreated(params) {
    return { evid: 11, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'collectionUpdated' event (evid: 12)
 *
 * Event description: When a portfolio collection updated.
 * 2 ways to trigger:
 * 1. from the collection page itself
 * 2. in the list of collections, we can click and drag cards to change the sort order of collections
 *
 * @param {collectionUpdatedParams} params - The parameters for this event
 * @property {string} params - Json of:Name, Description
 * @property {string} collectionId - Collection ID
 * @property {string} section - the section in the screen that the users clicked from.
 * @property {string} origin - the screen where the user clicked on 'save collection'.
 * @return {LogObject<collectionUpdatedParams>} LogObject<collectionUpdatedParams>
 */
export function collectionUpdated(params) {
    return { evid: 12, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickNewProjectOpenDropDown' event (evid: 19)
 *
 * Event description: triggered when the user click on the 'New Project' button which opens drop down
 *
 * @param {clickNewProjectOpenDropDownParams} params - The parameters for this event
 * @property {string} origin - The screen where the 'New Project' btn was clicked from
 * @return {LogObject<clickNewProjectOpenDropDownParams>} LogObject<clickNewProjectOpenDropDownParams>
 */
export function clickNewProjectOpenDropDown(params) {
    return { evid: 19, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'portfolioPageViewed' event (evid: 2)
 *
 * Event description: Every time a project/collection/portfolio page is viewed in different view modes (editor/preview/viewer)
 *
 * @param {portfolioPageViewedParams} params - The parameters for this event
 * @property {string} viewMode - editor/preview/viewer
 * @property {string} portfolioViewMode - editor/preview/viewer of portfolio
 * @property {string} pageName - project/collection/portfolio
 * @return {LogObject<portfolioPageViewedParams>} LogObject<portfolioPageViewedParams>
 */
export function portfolioPageViewed(params) {
    return { evid: 2, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickCreateProject' event (evid: 20)
 *
 * Event description: when the user clicks on create project
 *
 * @param {clickCreateProjectParams} params - The parameters for this event
 * @property {string} section - the section in the screen that the users clicked from.
 * @property {string} origin - the screen where the user clicked on 'create project'.
 * @return {LogObject<clickCreateProjectParams>} LogObject<clickCreateProjectParams>
 */
export function clickCreateProject(params) {
    return { evid: 20, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'projectCreated' event (evid: 21)
 *
 * Event description: When a portfolio project is first created in the DB
 *
 * @param {projectCreatedParams} params - The parameters for this event
 * @property {GUID} previewProjectId - if the project is created with AI, project id of the preview project
 * @property {string} params - Json of: Title, Details, Description, Additional Info
 * @property {string} collections - list of collection IDs that the project belongs to
 * @property {string} projectId - project ID
 * @property {string} mediaItems - If created with AI - media items ids
 * @property {string} origin - the screen where the project was created from
 * @property {number} mediaCount - num of media items
 * @return {LogObject<projectCreatedParams>} LogObject<projectCreatedParams>
 */
export function projectCreated(params) {
    return { evid: 21, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'projectUpdated' event (evid: 40)
 *
 * Event description: will be sent whenever the user made changes in his project and then save them.
 *
 * @param {projectUpdatedParams} params - The parameters for this event
 * @property {string} params - Json of: Title, Details, Description, Additional Info
 * @property {string} collections - collections
 * @property {string} projectId - project ID
 * @property {string} origin - the screen where the user clicked on 'create project'.
 * @property {number} mediaCount - num of media items in project
 * @return {LogObject<projectUpdatedParams>} LogObject<projectUpdatedParams>
 */
export function projectUpdated(params) {
    return { evid: 40, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'openManageMediaModal' event (evid: 42)
 *
 * Event description: open manage media modal from one of the entry points
 *
 * @param {openManageMediaModalParams} params - The parameters for this event
 * @property {string} projectId - project id
 * @property {string} buttonName - manage media/image thumbnail
 * @property {string} pageName - project page
 * @return {LogObject<openManageMediaModalParams>} LogObject<openManageMediaModalParams>
 */
export function openManageMediaModal(params) {
    return { evid: 42, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'uploadMediaStarted' event (evid: 43)
 *
 * Event description: Upload batch of media to project started
 *
 * @param {uploadMediaStartedParams} params - The parameters for this event
 * @property {number} itemsCount - number of items in batch
 * @property {string} projectId - project id
 * @property {string} origin - screen that the upload originated in
 * @property {string} itemType - array of items type and count
 * @return {LogObject<uploadMediaStartedParams>} LogObject<uploadMediaStartedParams>
 */
export function uploadMediaStarted(params) {
    return { evid: 43, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'uploadMediaFinished' event (evid: 44)
 *
 * Event description: Upload batch of media to project finished
 *
 * @param {uploadMediaFinishedParams} params - The parameters for this event
 * @property {number} itemsCount - number of items in batch
 * @property {string} projectId - project id
 * @property {string} origin - screen that the upload originated in
 * @property {string} itemType - array of items type and count
 * @return {LogObject<uploadMediaFinishedParams>} LogObject<uploadMediaFinishedParams>
 */
export function uploadMediaFinished(params) {
    return { evid: 44, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'errorInMediaFileUpload' event (evid: 45)
 *
 * Event description: Media upload failed - error reason
 *
 * @param {errorInMediaFileUploadParams} params - The parameters for this event
 * @property {string} mediaType - type of media file
 * @property {string} errorMsg - error message
 * @property {string} projectId - project id
 * @return {LogObject<errorInMediaFileUploadParams>} LogObject<errorInMediaFileUploadParams>
 */
export function errorInMediaFileUpload(params) {
    return { evid: 45, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickConnect' event (evid: 50)
 *
 * Event description: Every time user clicked on connect. Two trigger points, one from new project drop down and the other from sync project page.
 *
 * @param {clickConnectParams} params - The parameters for this event
 * @property {string} section - the section in the screen that the users clicked from.
 * @property {string} sourceName - the source name is the platform the user want to connect to (dribble, instagram, etc...)
 * @property {string} origin - the screen where the project was synced from.
 * @return {LogObject<clickConnectParams>} LogObject<clickConnectParams>
 */
export function clickConnect(params) {
    return { evid: 50, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickSyncProject' event (evid: 51)
 *
 * Event description: Each time the user clicked on sync project.
 *
 * @param {clickSyncProjectParams} params - The parameters for this event
 * @property {string} sourceName - the source platform of the project
 * @property {string} title - the title of the project
 * @property {number} mediaCount - the number of media items that the synced project has
 * @return {LogObject<clickSyncProjectParams>} LogObject<clickSyncProjectParams>
 */
export function clickSyncProject(params) {
    return { evid: 51, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickUnsyncRemoveProject' event (evid: 52)
 *
 * Event description: Each time the user clicked on remove synced project from his portfolio
 *
 * @param {clickUnsyncRemoveProjectParams} params - The parameters for this event
 * @property {string} params - Json of: Title, Details, Description, Additional Info
 * @property {string} collections - list of collection IDs that the project belongs to
 * @property {string} projectId - project ID
 * @property {string} sourceName - the source platform of the project
 * @property {string} origin - the screen where the project was created from
 * @property {number} mediaCount - num of media items
 * @return {LogObject<clickUnsyncRemoveProjectParams>} LogObject<clickUnsyncRemoveProjectParams>
 */
export function clickUnsyncRemoveProject(params) {
    return { evid: 52, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickConnectAppFromNewProjectButton' event (evid: 53)
 *
 * Event description: Triggered when user clicks on 'Connect from an App' from 'New Project' drop down
 *
 * @param {clickConnectAppFromNewProjectButtonParams} params - The parameters for this event
 * @return {LogObject<clickConnectAppFromNewProjectButtonParams>} LogObject<clickConnectAppFromNewProjectButtonParams>
 */
export function clickConnectAppFromNewProjectButton(params) {
    return { evid: 53, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'requestOtherIntegration' event (evid: 54)
 *
 * Event description: when the user interacts with the integrations question - requesting an app that we currently do not support
 *
 * @param {requestOtherIntegrationParams} params - The parameters for this event
 * @property {string} extraInfo - any other comments
 * @property {string} buttonName - exit/done/looking for different app/question mark/cancel
 * @property {string} value - array of chosen apps
 * @return {LogObject<requestOtherIntegrationParams>} LogObject<requestOtherIntegrationParams>
 */
export function requestOtherIntegration(params) {
    return { evid: 54, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickGetStartedIntegration' event (evid: 59)
 *
 * Event description: Triggered when user clicked 'Get Started' on the internal landing page of portfolio integrations
 *
 * @param {clickGetStartedIntegrationParams} params - The parameters for this event
 * @return {LogObject<clickGetStartedIntegrationParams>} LogObject<clickGetStartedIntegrationParams>
 */
export function clickGetStartedIntegration(params) {
    return { evid: 59, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'actionInFunnelMicroActions' event (evid: 60)
 *
 * Event description: user does an action in the Portfolio funnel micro action steps
 *
 * @param {actionInFunnelMicroActionsParams} params - The parameters for this event
 * @property {string} fieldName - name, description, type, date, location, label, click upload media
 * @return {LogObject<actionInFunnelMicroActionsParams>} LogObject<actionInFunnelMicroActionsParams>
 */
export function actionInFunnelMicroActions(params) {
    return { evid: 60, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'actionInHomeMicroActions' event (evid: 61)
 *
 * Event description: user does an action in the Portfolio setup steps micro action in home screen
 *
 * @param {actionInHomeMicroActionsParams} params - The parameters for this event
 * @property {string} fieldName - name, description, type, date, location, label, click upload media
 * @return {LogObject<actionInHomeMicroActionsParams>} LogObject<actionInHomeMicroActionsParams>
 */
export function actionInHomeMicroActions(params) {
    return { evid: 61, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'projectsListEmptyStateLoaded' event (evid: 62)
 *
 * Event description: Projects List Page Loaded in Empty State
 *
 * @param {projectsListEmptyStateLoadedParams} params - The parameters for this event
 * @return {LogObject<projectsListEmptyStateLoadedParams>} LogObject<projectsListEmptyStateLoadedParams>
 */
export function projectsListEmptyStateLoaded(params) {
    return { evid: 62, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'collectionPageEmptyStateLoaded' event (evid: 63)
 *
 * Event description: Collection Page loaded with zero projects
 *
 * @param {collectionPageEmptyStateLoadedParams} params - The parameters for this event
 * @return {LogObject<collectionPageEmptyStateLoadedParams>} LogObject<collectionPageEmptyStateLoadedParams>
 */
export function collectionPageEmptyStateLoaded(params) {
    return { evid: 63, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'generalActionInWizard' event (evid: 64)
 *
 * Event description: user does a general action in the Portfolio create project wizard
 *
 * @param {generalActionInWizardParams} params - The parameters for this event
 * @property {string} screenName - wizard screen
 * @property {string} action - action name: next/back/skip...
 * @return {LogObject<generalActionInWizardParams>} LogObject<generalActionInWizardParams>
 */
export function generalActionInWizard(params) {
    return { evid: 64, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'wizardScreenViewed' event (evid: 65)
 *
 * Event description: Wizard screen is shown to the user
 *
 * @param {wizardScreenViewedParams} params - The parameters for this event
 * @property {string} screenName - the wizard screen shown to the user
 * @return {LogObject<wizardScreenViewedParams>} LogObject<wizardScreenViewedParams>
 */
export function wizardScreenViewed(params) {
    return { evid: 65, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'clickUploadMediaInWizard' event (evid: 66)
 *
 * Event description: the user clicked on upload media button
 *
 * @param {clickUploadMediaInWizardParams} params - The parameters for this event
 * @property {string} screenName - wizard screen name
 * @property {string} buttonName - name of the add media button
 * @return {LogObject<clickUploadMediaInWizardParams>} LogObject<clickUploadMediaInWizardParams>
 */
export function clickUploadMediaInWizard(params) {
    return { evid: 66, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'screenAnswerOnWizard' event (evid: 67)
 *
 * Event description: the user's answer to the required fields in project wizard.
 *
 * @param {screenAnswerOnWizardParams} params - The parameters for this event
 * @property {string} answer - jason with field and user's answer
 * @property {string} screenName - Wizard screen
 * @return {LogObject<screenAnswerOnWizardParams>} LogObject<screenAnswerOnWizardParams>
 */
export function screenAnswerOnWizard(params) {
    return { evid: 67, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'wizardLoaded' event (evid: 68)
 *
 * Event description: Wizard is loaded
 *
 * @param {wizardLoadedParams} params - The parameters for this event
 * @property {string} origin - screen leaded to open wizard
 * @return {LogObject<wizardLoadedParams>} LogObject<wizardLoadedParams>
 */
export function wizardLoaded(params) {
    return { evid: 68, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'uploadMediaItemStart' event (evid: 70)
 *
 * Event description: when a media item is chosen and upload to portfolio begins
 *
 * @param {uploadMediaItemStartParams} params - The parameters for this event
 * @property {string} mediaType - image/video/etc
 * @property {string} source - computer/media manager
 * @property {string} projectId - project id (can be null if project is new)
 * @property {string} itemId - item id
 * @return {LogObject<uploadMediaItemStartParams>} LogObject<uploadMediaItemStartParams>
 */
export function uploadMediaItemStart(params) {
    return { evid: 70, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'uploadMediaItemFinish' event (evid: 71)
 *
 * Event description: when a media item finished uploading to portfolio project
 *
 * @param {uploadMediaItemFinishParams} params - The parameters for this event
 * @property {string} mediaType - image/video/etc
 * @property {string} source - computer/media manager
 * @property {string} projectId - project id (can't be null)
 * @property {string} itemId - item id
 * @return {LogObject<uploadMediaItemFinishParams>} LogObject<uploadMediaItemFinishParams>
 */
export function uploadMediaItemFinish(params) {
    return { evid: 71, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'buttonClickInBo' event (evid: 72)
 *
 * Event description: button clicked in one of Portfolio BO pages
 *
 * @param {buttonClickInBoParams} params - The parameters for this event
 * @property {string} collectionId - collection id
 * @property {string} projectId - project id
 * @property {string} buttonName - button
 * @property {string} pageName - Project/Collections/etc
 * @property {string} value - extra info of the button
 * @return {LogObject<buttonClickInBoParams>} LogObject<buttonClickInBoParams>
 */
export function buttonClickInBo(params) {
    return { evid: 72, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'errorCreatingProjectInWizard' event (evid: 73)
 *
 * Event description: Error Creating Project in Wizard last screen (project id was not created)
 *
 * @param {errorCreatingProjectInWizardParams} params - The parameters for this event
 * @property {string} type - error type
 * @return {LogObject<errorCreatingProjectInWizardParams>} LogObject<errorCreatingProjectInWizardParams>
 */
export function errorCreatingProjectInWizard(params) {
    return { evid: 73, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiDescGeneratorReachedLimit' event (evid: 74)
 *
 * Event description: user reached to AI generator usage limit
 *
 * @param {aiDescGeneratorReachedLimitParams} params - The parameters for this event
 * @property {string} time_period - limit time period
 * @property {number} usageLimit - limit defined
 * @property {string} projectId - project id
 * @return {LogObject<aiDescGeneratorReachedLimitParams>} LogObject<aiDescGeneratorReachedLimitParams>
 */
export function aiDescGeneratorReachedLimit(params) {
    return { evid: 74, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'boPageView' event (evid: 75)
 *
 * Event description: generic BO specific page view (not all portfolio BO pages)
 *
 * @param {boPageViewParams} params - The parameters for this event
 * @property {string} pageName - Page name
 * @return {LogObject<boPageViewParams>} LogObject<boPageViewParams>
 */
export function boPageView(params) {
    return { evid: 75, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'projectCoverUpdated' event (evid: 76)
 *
 * Event description: Project Cover was updated (new media item was set as a cover)
 *
 * @param {projectCoverUpdatedParams} params - The parameters for this event
 * @property {string} mediaType - Cover media type (IMAGE / VIDEO ... )
 * @property {string} projectId - project id
 * @property {string} itemId - Project cover item id
 * @return {LogObject<projectCoverUpdatedParams>} LogObject<projectCoverUpdatedParams>
 */
export function projectCoverUpdated(params) {
    return { evid: 76, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'itemAltTextUpdatedFromOrganizeMedia' event (evid: 77)
 *
 * Event description: portfolio project media Item alt text updated from project organize media
 *
 * @param {itemAltTextUpdatedFromOrganizeMediaParams} params - The parameters for this event
 * @property {string} newAltText - new item alt text
 * @property {string} projectId - project id (can be null if project is new)
 * @property {string} itemId - item id
 * @property {string} previousAltText - previous_alt_text
 * @return {LogObject<itemAltTextUpdatedFromOrganizeMediaParams>} LogObject<itemAltTextUpdatedFromOrganizeMediaParams>
 */
export function itemAltTextUpdatedFromOrganizeMedia(params) {
    return { evid: 77, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'itemAltTextWhenOpenOrganizeMedia' event (evid: 78)
 *
 * Event description: Item alt text when organize media open
 *
 * @param {itemAltTextWhenOpenOrganizeMediaParams} params - The parameters for this event
 * @property {string} altText - item alt text
 * @property {string} projectId - project id (can be null if project is new)
 * @property {string} itemId - item id
 * @return {LogObject<itemAltTextWhenOpenOrganizeMediaParams>} LogObject<itemAltTextWhenOpenOrganizeMediaParams>
 */
export function itemAltTextWhenOpenOrganizeMedia(params) {
    return { evid: 78, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'altTextWhenOrganizeMediaClosed' event (evid: 79)
 *
 * Event description: Item alt text when organize media closed
 *
 * @param {altTextWhenOrganizeMediaClosedParams} params - The parameters for this event
 * @property {string} altText - item alt text
 * @property {string} projectId - project id (can be null if project is new)
 * @property {string} itemId - item id
 * @return {LogObject<altTextWhenOrganizeMediaClosedParams>} LogObject<altTextWhenOrganizeMediaClosedParams>
 */
export function altTextWhenOrganizeMediaClosed(params) {
    return { evid: 79, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupScreenViewed' event (evid: 82)
 *
 * Event description: Step page is viewed in the portfolio AI creation flow
 *
 * @param {aiSetupScreenViewedParams} params - The parameters for this event
 * @property {string} screenName - screen name
 * @property {number} projectsCount - number of projects shown in projects preview screen
 * @return {LogObject<aiSetupScreenViewedParams>} LogObject<aiSetupScreenViewedParams>
 */
export function aiSetupScreenViewed(params) {
    return { evid: 82, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupGeneralClick' event (evid: 83)
 *
 * Event description: General Click in the portfolio AI creation flow
 *
 * @param {aiSetupGeneralClickParams} params - The parameters for this event
 * @property {string} screenName - screen name
 * @property {string} button_name - button name (next / cancel /..)
 * @property {string} answer - screen answer by filled by the user
 * @return {LogObject<aiSetupGeneralClickParams>} LogObject<aiSetupGeneralClickParams>
 */
export function aiSetupGeneralClick(params) {
    return { evid: 83, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupEditProjectField' event (evid: 89)
 *
 * Event description: User edit preview project title or description
 *
 * @param {aiSetupEditProjectFieldParams} params - The parameters for this event
 * @property {GUID} previewProjectId - preview project id
 * @property {string} field - edited field
 * @property {string} fieldValue - field new value filled by the user
 * @return {LogObject<aiSetupEditProjectFieldParams>} LogObject<aiSetupEditProjectFieldParams>
 */
export function aiSetupEditProjectField(params) {
    return { evid: 89, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupDragAndDropMediaItem' event (evid: 90)
 *
 * Event description: User drag and drop a media item to a different project
 *
 * @param {aiSetupDragAndDropMediaItemParams} params - The parameters for this event
 * @property {GUID} fromProject - previous project id of the media item
 * @property {GUID} toProject - new project id of the media item
 * @property {string} mediaCaption - media item caption
 * @property {string} itemId - media item id
 * @return {LogObject<aiSetupDragAndDropMediaItemParams>} LogObject<aiSetupDragAndDropMediaItemParams>
 */
export function aiSetupDragAndDropMediaItem(params) {
    return { evid: 90, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupDeletePreviewProject' event (evid: 91)
 *
 * Event description: User deleted project suggested by the AI (optional only when the project has no photos)
 *
 * @param {aiSetupDeletePreviewProjectParams} params - The parameters for this event
 * @property {GUID} previewProjectId - preview project id
 * @property {string} description - project description
 * @property {string} title - project title
 * @property {string} type - click delete  / project deleted
 * @return {LogObject<aiSetupDeletePreviewProjectParams>} LogObject<aiSetupDeletePreviewProjectParams>
 */
export function aiSetupDeletePreviewProject(params) {
    return { evid: 91, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupPopUpIsShown' event (evid: 92)
 *
 * Event description: Pop up modal is shown to the user in the AI creation flow
 *
 * @param {aiSetupPopUpIsShownParams} params - The parameters for this event
 * @property {string} popupName - pop up name
 * @return {LogObject<aiSetupPopUpIsShownParams>} LogObject<aiSetupPopUpIsShownParams>
 */
export function aiSetupPopUpIsShown(params) {
    return { evid: 92, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupPopUpClick' event (evid: 93)
 *
 * Event description: Pop up modal click in the AI creation flow
 *
 * @param {aiSetupPopUpClickParams} params - The parameters for this event
 * @property {string} buttonName - button name
 * @property {string} popupName - pop up name
 * @return {LogObject<aiSetupPopUpClickParams>} LogObject<aiSetupPopUpClickParams>
 */
export function aiSetupPopUpClick(params) {
    return { evid: 93, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupFeedbackSubmitted' event (evid: 94)
 *
 * Event description: User submitted portfolio feedback
 *
 * @param {aiSetupFeedbackSubmittedParams} params - The parameters for this event
 * @property {string} question - name of feedback required
 * @property {string} answer - answer submitted by the user (yes / no / ..)
 * @return {LogObject<aiSetupFeedbackSubmittedParams>} LogObject<aiSetupFeedbackSubmittedParams>
 */
export function aiSetupFeedbackSubmitted(params) {
    return { evid: 94, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupMediaItemDeleted' event (evid: 95)
 *
 * Event description: media item was deleted
 *
 * @param {aiSetupMediaItemDeletedParams} params - The parameters for this event
 * @property {string} screenName - deleted action screen name
 * @property {string} itemId - media item id
 * @return {LogObject<aiSetupMediaItemDeletedParams>} LogObject<aiSetupMediaItemDeletedParams>
 */
export function aiSetupMediaItemDeleted(params) {
    return { evid: 95, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupStartUploadMedia' event (evid: 96)
 *
 * Event description: Uploading media from computer process started
 *
 * @param {aiSetupStartUploadMediaParams} params - The parameters for this event
 * @property {number} itemsCount - Number of media items sent to upload
 * @return {LogObject<aiSetupStartUploadMediaParams>} LogObject<aiSetupStartUploadMediaParams>
 */
export function aiSetupStartUploadMedia(params) {
    return { evid: 96, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupFinishUploadMedia' event (evid: 97)
 *
 * Event description: Uploading media from computer process finished
 *
 * @param {aiSetupFinishUploadMediaParams} params - The parameters for this event
 * @property {number} duration - media upload duration
 * @property {number} itemsCount - Number of media items sent to upload
 * @property {number} uploadSucessItems - Number of media items uploaded successfully
 * @property {number} uploadFailedItems - Number of media items failed to upload
 * @return {LogObject<aiSetupFinishUploadMediaParams>} LogObject<aiSetupFinishUploadMediaParams>
 */
export function aiSetupFinishUploadMedia(params) {
    return { evid: 97, src: 150, endpoint: 'portfolio-users', params: params };
}
/**
 * A function to create a log object for the 'aiSetupReachedUploadMediaLimit' event (evid: 98)
 *
 * Event description: User tries to exceed the upload limit
 *
 * @param {aiSetupReachedUploadMediaLimitParams} params - The parameters for this event
 * @property {number} itemsExist - Number of existing media items
 * @property {string} source - computer / media manager
 * @property {number} itemsLimis - Maximum number of media items allowed
 * @property {number} itemsUpload - Number of media items user tries to upload
 * @return {LogObject<aiSetupReachedUploadMediaLimitParams>} LogObject<aiSetupReachedUploadMediaLimitParams>
 */
export function aiSetupReachedUploadMediaLimit(params) {
    return { evid: 98, src: 150, endpoint: 'portfolio-users', params: params };
}
//# sourceMappingURL=index.js.map